import Button from "components/InvestorCommon/Button/Button";
import { FC, useEffect, useRef } from "react";
import { IMAGES } from "utils/images";
import { Actions, Card, CloseButton, Message, Overlay, Title } from "./Styled";

export interface IRiskProfileReminderPopupProps {
	open: boolean;
	isSubmitting?: boolean;
	/** Primary action — "Yes, I'll do it now" */
	onConfirm: () => void;
	/** Secondary action — "Remind me later" */
	onSnooze: () => void;
	/** Close icon (X) */
	onClose: () => void;
}

const TITLE = "Update Your Risk Profile";
const MESSAGE =
	"Your risk profile was last submitted more than 365 days ago. Please review and resubmit your Risk Profile Questionnaire to keep your investor profile up to date.";

const RiskProfileReminderPopup: FC<IRiskProfileReminderPopupProps> = ({
	open,
	isSubmitting = false,
	onConfirm,
	onSnooze,
	onClose,
}) => {
	const confirmRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (!open) return;

		confirmRef.current?.focus();

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape" && !isSubmitting) {
				onClose();
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [open, isSubmitting, onClose]);

	if (!open) return null;

	return (
		<Overlay>
			<Card
				role="dialog"
				aria-modal="true"
				aria-labelledby="risk-profile-reminder-title"
				aria-describedby="risk-profile-reminder-message"
				className="!px-6"
			>
				<CloseButton
					type="button"
					aria-label="Close"
					disabled={isSubmitting}
					onClick={onClose}
				>
					<img src={IMAGES.icon.close} width="14px" height="14px" alt="" />
				</CloseButton>

				<Title
					id="risk-profile-reminder-title"
					className="!text-[#181C1E] !text-2xl !font-semibold"
				>
					{TITLE}
				</Title>
				<Message
					id="risk-profile-reminder-message"
					className="!text-[#464652] !text-base !font-normal !mt-6 !mb-8"
				>
					{MESSAGE}
				</Message>

				<Actions>
					<Button theme="primary" size="small" disabled={isSubmitting} onClick={onSnooze}>
						Remind me later
					</Button>
					<Button
						ref={confirmRef}
						theme="secondary"
						size="small"
						disabled={isSubmitting}
						onClick={onConfirm}
					>
						Yes, I&apos;ll do it now
					</Button>
				</Actions>
			</Card>
		</Overlay>
	);
};

export default RiskProfileReminderPopup;
